import React, { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Avatar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import ResourceForm from '../components/ResourceForm';
import * as api from '../services/api';
import type { Risorsa, RisorsaInput } from '../types';
import { useAuth } from '../context/AuthContext';

const RisorsePage: React.FC = () => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDisabled, setShowDisabled] = useState<'enabled' | 'all'>('enabled');
  const [risorse, setRisorse] = useState<Risorsa[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingRisorsa, setEditingRisorsa] = useState<Risorsa | null>(null);

  const loadRisorse = async () => {
    try {
      const data = await api.getRisorse(showDisabled === 'all');
      setRisorse(data);
    } catch (error) {
      console.error('Error loading risorse:', error);
    }
  };

  useEffect(() => {
    loadRisorse();
  }, [showDisabled]);

  const filteredRisorse = useMemo(() => {
    if (!searchTerm) return risorse;

    return risorse.filter((r) => {
      const skillNames = r.skills ? r.skills.map(s => s.nome).join(' ') : '';
      const searchString = `${r.nome} ${r.cognome || ''} ${r.email || ''} ${r.struttura || ''} ${skillNames}`.toLowerCase();
      return searchString.includes(searchTerm.toLowerCase());
    });
  }, [risorse, searchTerm]);

  const handleAdd = () => {
    setEditingRisorsa(null);
    setFormOpen(true);
  };

  const handleEdit = (risorsa: Risorsa) => {
    setEditingRisorsa(risorsa);
    setFormOpen(true);
  };

  const handleDelete = async (risorsa: Risorsa) => {
    if (!window.confirm(`Vuoi disabilitare la risorsa "${risorsa.nome} ${risorsa.cognome || ''}"?`)) {
      return;
    }

    try {
      await api.deleteRisorsa(risorsa.id);
      await loadRisorse();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Errore nell\'eliminazione della risorsa');
    }
  };

  const handleSave = async (risorsaInput: RisorsaInput, id?: number) => {
    try {
      if (id) {
        await api.updateRisorsa(id, risorsaInput);
      } else {
        await api.createRisorsa(risorsaInput);
      }
      await loadRisorse();
      setFormOpen(false);
      setEditingRisorsa(null);
    } catch (error) {
      console.error('Error saving risorsa:', error);
      alert('Errore nel salvataggio della risorsa');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">
            Gestione Risorse
          </Typography>
          {hasPermission('risorse', 'create') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
            >
              Nuova Risorsa
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            size="small"
            placeholder="Cerca risorse..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />

          <ToggleButtonGroup
            value={showDisabled}
            exclusive
            onChange={(_, value) => value && setShowDisabled(value)}
            size="small"
          >
            <ToggleButton value="enabled">Solo Abilitati</ToggleButton>
            <ToggleButton value="all">Tutti</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Foto</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Cognome</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Struttura</TableCell>
                <TableCell>ID HRP</TableCell>
                <TableCell>ID ERP</TableCell>
                <TableCell>Figura Professionale</TableCell>
                <TableCell>Skills</TableCell>
                <TableCell>Costo Orario</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRisorse.map((risorsa) => (
                <TableRow key={risorsa.id} hover>
                  <TableCell>
                    <Avatar src={risorsa.foto || undefined} sx={{ width: 40, height: 40 }}>
                      {!risorsa.foto && <PersonIcon />}
                    </Avatar>
                  </TableCell>
                  <TableCell>{risorsa.nome}</TableCell>
                  <TableCell>{risorsa.cognome || '-'}</TableCell>
                  <TableCell>{risorsa.email || '-'}</TableCell>
                  <TableCell>{risorsa.struttura || '-'}</TableCell>
                  <TableCell>{risorsa.id_hrp || '-'}</TableCell>
                  <TableCell>{risorsa.id_erp || '-'}</TableCell>
                  <TableCell>{risorsa.figura_professionale_nome || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {risorsa.skills && risorsa.skills.length > 0 ? (
                        risorsa.skills.map((skill) => (
                          <Chip
                            key={skill.id}
                            label={`${skill.nome} (${skill.livello})`}
                            size="small"
                            variant="outlined"
                          />
                        ))
                      ) : (
                        <span>-</span>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>€ {risorsa.costo_orario?.toFixed(2) || '0.00'}</TableCell>
                  <TableCell>
                    {risorsa.enabled ? (
                      <Chip icon={<CheckCircleIcon />} label="Attivo" color="success" size="small" />
                    ) : (
                      <Chip icon={<CancelIcon />} label="Disabilitato" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {hasPermission('risorse', 'update') && (
                      <IconButton size="small" onClick={() => handleEdit(risorsa)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {hasPermission('risorse', 'delete') && (
                      <IconButton size="small" onClick={() => handleDelete(risorsa)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filteredRisorse.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nessuna risorsa trovata
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <ResourceForm
        open={formOpen}
        risorsa={editingRisorsa}
        onClose={() => {
          setFormOpen(false);
          setEditingRisorsa(null);
        }}
        onSave={handleSave}
      />
    </Container>
  );
};

export default RisorsePage;
